import { ChangeDetectorRef, Component, HostListener, Inject, OnDestroy,
         OnInit, ViewChild, Input, ViewEncapsulation
       } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ProgressOverlayComponent, ProgressType
       } from 'app/controls/progress-overlay/progress-overlay.component';
import { GalleryService } from 'app/gallery/gallery.service';
import { importImageStoreImage } from 'app/gallery/imageprovider';
import { ConfigService } from 'core/config/config.service';
import { GlobalConstants, ProgressConstants } from 'core/constants';
import { CriticalError, TrivialError } from 'core/error/error';
import { ErrorService } from 'core/error/error.service';
import { Globals } from 'core/globals';
import { ImageStoreService } from 'core/imagestore/imagestore.service';
import { ProductService } from 'core/product/product.service';
import { EventTrackingService, TrackingObject
       } from 'core/tracking/event-tracking.service';
import { Utils } from 'core/utils';
import { AsyncEventEmitterSubscriber } from 'core/utils/async-event-emitter';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { ProductConfig } from './product-designer-config';
import { ProductDesignerService } from './product-designer.service';
import { Progress, ProgressService } from './progress/progress.service';

class NotInStockError extends TrivialError
{
  // i18n.error.notInStockError.title
  // i18n.error.notInStockError.message
  protected _name: string = 'NotInStockError';
}

class DeprecatedBrowserError extends TrivialError
{
  // i18n.error.deprecatedBrowserError.title
  // i18n.error.deprecatedBrowserError.message
  protected _name: string = 'DeprecatedBrowserError';
}

class StartupError extends CriticalError
{
  protected _name: string = 'StartupError';
}

@Component({
    selector: 'product-designer',
    templateUrl: './product-designer.component.html',
    styleUrls: ['./product-designer.component.scss'],
    standalone: false,
    encapsulation: ViewEncapsulation.ShadowDom
})
export class ProductDesignerComponent implements OnDestroy, OnInit
{
  @Input() config?: string;

  @ViewChild('collage-designer') public collageDesigner: any;
  @ViewChild('progressOverlay', { static: true }) private progressOverlay:
      ProgressOverlayComponent;

  public progressConstants: ProgressConstants;

  public currentProduct: ProductConfig;
  public disableDesigner: boolean = false;
  public progress: Progress = {
      id: undefined,
      group: undefined,
      groupMember: undefined,
      title: ''
  };

  private subscriptions: Subscription = new Subscription();
  private productHasChangedSubscription:
      AsyncEventEmitterSubscriber<ProductConfig>;

  constructor(
      private route: ActivatedRoute,
      private cdr: ChangeDetectorRef,
      @Inject(ProductDesignerService) public productDesignerService:
          ProductDesignerService,
      @Inject(ConfigService) private configService: ConfigService,
      @Inject(ErrorService) private errorService: ErrorService,
      @Inject(ProgressService) private progressService: ProgressService,
      @Inject(ProductService) private productService: ProductService,
      @Inject(ImageStoreService) private imageStoreService,
      @Inject(GalleryService) private galleryService,
      @Inject(EventTrackingService) private eventTrackingService:
          EventTrackingService
  )
  { }

  public async ngOnInit(): Promise<void>
  {
    this.subscriptions.add(
        this.productDesignerService.suspendDesignerTrigger
          .subscribe(suspendConfig => {
            if (suspendConfig.suspend) {
              this.progressOverlay.startProgress(suspendConfig.progressType);
            }
            else {
              if (suspendConfig.finished) {
                this.progressOverlay.finishProgress(
                    suspendConfig.progressType);
              } else {
                this.progressOverlay.cancelProgress(
                  suspendConfig.progressType);
              }
            }
            this.cdr.detectChanges();
          }));

    this.progressConstants = ProgressConstants;
    this.productDesignerService.suspendDesignerTrigger
      .emit({suspend: true, progressType: ProgressType.Progress});

    this.subscriptions.add(
        this.errorService.criticalErrorOccurred.subscribe((criticalError) => {
          this.progressOverlay.cancelProgress(undefined, true);
          this.disableDesigner = criticalError !== undefined;
        }));

    this.subscriptions.add(
        this.progressService.progressChanged.subscribe((progress) => {
          this.progress = progress;
        }));

    this.productHasChangedSubscription =
        this.productService.productHasChanged.subscribe(
            async (productConfig) => {
              this.progressOverlay.cancelProgress(undefined, true);
              this.disableDesigner = false;

              if (!productConfig.inStock &&
                  Globals.mode === GlobalConstants.MODE_DEFAULT) {
                this.errorService.add(
                    new NotInStockError(undefined, [], false),
                    'NotInStockError');
                this.errorService.flush('NotInStockError');
              }

              this.cdr.detectChanges();
              this.currentProduct = productConfig;
              this.errorService.clear();
        });

    if (this.config !== undefined) {
      await this.loadConfig(this.config);
    } else {
      const params = await this.route.queryParams
        .pipe(
          filter(p => !!p.config),
          take(1)
        )
        .toPromise();
      await this.loadConfig(params.config);
    }

    Utils.ChangeDetection.setHandle(this.cdr);
  }

  private async loadConfig(config: string): Promise<void>
  {
    const importImage = async image_data => {
      try {
        const info = await this.imageStoreService.getImageInfo(image_data.url);
        await this.galleryService.addPendingImage(importImageStoreImage(info));
      }
      catch (e) {
        this.errorService.add(
          new StartupError('could not import images'),
          'ProductDesignerComponent'
        );
        this.errorService.flush('ProductDesignerComponent');
      }
    };

    const productDesignerConfig =
        await this.configService.resolveProductDesignerConfig(config);

    if (!productDesignerConfig) {
      return;
    }

    if (Utils.isDeprecatedBrowser()) {
      this.errorService.add(
          new DeprecatedBrowserError(undefined, [], false),
          'DeprecatedBrowserError');
      this.errorService.flush('DeprecatedBrowserError');
    }

    this.productDesignerService.config = productDesignerConfig;
    this.setCSSConstants();
    await this.productService.setProducts(
        productDesignerConfig.products,
        productDesignerConfig.selectedProduct);
    this.trackProductDesignerStart();
    if (productDesignerConfig.importImages) {
      await Promise.all(productDesignerConfig.importImages.map(importImage));
    }
  }

  public ngOnDestroy(): void
  {
    this.subscriptions.unsubscribe();
    this.productService.productHasChanged.unsubscribe(
        this.productHasChangedSubscription);
  }

  @HostListener('window:beforeunload', ['$event'])
  public beforeUnload(event: any): any {
    // Entgegen der Dokumentation von MDN muss hier kein String sondern ein
    // boolean zurückgegeben werden. Das liegt daran, dass angular bindings den
    // Rückgabewert verändern.
    return this.progressService.isFinalized;
  }

  private trackProductDesignerStart(): void
  {
    const getStartTemplate = () => {
      const templateGroups = this.productService
        .getCurrentProduct().collageDesignerConfig
        .steps[0].parts[0].templateGroups;
      for (const templateGroup of templateGroups) {
        if (templateGroup.isDefault) {
          const templates = templateGroup.templates;
          for (let templateId in templates) {
            if (templates[templateId].isDefault) {
              return templateId;
            }
          }
        }
      }
      return undefined;
    };
    const startTemplate = getStartTemplate();

    let trackObject = <TrackingObject> {
      category: this.productService.getCurrentProduct().id,
      action: 'start',
    };
    if (startTemplate) {
      trackObject['label'] = startTemplate;
    }

    this.eventTrackingService.track(trackObject);
  }

  private setCSSConstants(): void
  {
    const locale =
        this.configService.getProductDesignerComponentConfig().locale;
    if (['cs-CZ', 'sk-SK', 'pl-PL'].indexOf(locale) !== -1) {
      document.documentElement.style.setProperty(
          '--dynamic-font-family-decorative', 'Caveat');
    }
  }
}
